import { useEffect, useRef, useState } from 'react';
import { HelpTooltip } from '../components/HelpTooltip/HelpTooltip';
import type { Class, ClassCaptureSettings, ProjectKind } from '../data/types';
import { defaultSettingsFor } from './captureSettings';
import './CaptureSettingsDialog.css';

export interface CaptureSettingsDialogProps {
  cls: Class;
  kind: ProjectKind;
  onSave: (settings: ClassCaptureSettings) => void | Promise<void>;
  /** Fires when the kid flips the disabled-class switch. Saved
   *  immediately (not on Save Settings) since toggling disabled is a
   *  one-click affordance, not a multi-field commit. */
  onToggleDisabled?: (disabled: boolean) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * TM-style per-class capture settings. Image classes get FPS + a hold-
 * to-record toggle (timer mode adds delay + duration); audio classes
 * get delay + duration. Save commits via storage; Cancel discards. The
 * dialog is rendered inline (not a modal) so the kid stays oriented to
 * the class card it belongs to.
 */
export function CaptureSettingsDialog({
  cls,
  kind,
  onSave,
  onToggleDisabled,
  onCancel,
}: CaptureSettingsDialogProps): JSX.Element {
  const defaults = defaultSettingsFor(cls, kind);
  const initial = { ...defaults, ...(cls.captureSettings ?? {}) };
  const [delaySeconds, setDelaySeconds] = useState<number>(initial.delaySeconds);
  const [durationSeconds, setDurationSeconds] = useState<number>(initial.durationSeconds);
  const [fps, setFps] = useState<number>(initial.fps);
  const [holdToRecord, setHoldToRecord] = useState<boolean>(initial.holdToRecord);
  const [submitting, setSubmitting] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const handleSave = async (): Promise<void> => {
    setSubmitting(true);
    try {
      // Build a minimal settings object — only persist fields that
      // diverge from the kind-specific defaults so a "fresh" class
      // isn't haunted by stale overrides.
      const next: ClassCaptureSettings = {};
      if (delaySeconds !== defaults.delaySeconds) next.delaySeconds = delaySeconds;
      if (durationSeconds !== defaults.durationSeconds) next.durationSeconds = durationSeconds;
      if (kind === 'image' || kind === 'pose') {
        if (fps !== defaults.fps) next.fps = fps;
        if (holdToRecord !== defaults.holdToRecord) next.holdToRecord = holdToRecord;
      }
      await onSave(next);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bl-cap-settings" role="dialog" aria-label={`Capture settings for ${cls.name}`}>
      <p className="bl-cap-settings__title">Settings</p>

      {(kind === 'image' || kind === 'pose') && (
        <>
          <label className="bl-cap-settings__row">
            <span className="bl-cap-settings__label">
              FPS
              <HelpTooltip label="What is FPS?">
                Frames per second. This is how many pictures the camera takes each second while you
                are holding the record button. More frames means a richer dataset but more samples
                to manage.
              </HelpTooltip>
            </span>
            <input
              ref={firstFieldRef}
              type="number"
              min={1}
              max={30}
              step={1}
              value={fps}
              onChange={(e) => setFps(Number.parseInt(e.target.value, 10) || defaults.fps)}
              aria-label="FPS"
            />
          </label>
          <label className="bl-cap-settings__row bl-cap-settings__row--toggle">
            <span className="bl-cap-settings__label">
              Hold to record
              <HelpTooltip label="What does Hold to Record do?">
                When on, you press and hold the capture button to record a burst. When off, one
                click waits for the delay, then records for the duration you set.
              </HelpTooltip>
            </span>
            <input
              type="checkbox"
              checked={holdToRecord}
              onChange={(e) => setHoldToRecord(e.target.checked)}
              aria-label="Hold to record"
            />
          </label>
        </>
      )}

      <label className="bl-cap-settings__row">
        <span className="bl-cap-settings__label">
          Delay
          <HelpTooltip label="What does Delay do?">
            How many seconds to wait before recording starts. Helpful when you need a moment to get
            into position.
          </HelpTooltip>
        </span>
        <span className="bl-cap-settings__input-with-unit">
          <input
            ref={kind === 'audio' ? firstFieldRef : undefined}
            type="number"
            min={0}
            max={10}
            step={1}
            value={delaySeconds}
            onChange={(e) => setDelaySeconds(Number.parseInt(e.target.value, 10) || 0)}
            aria-label="Delay (seconds)"
          />
          <span className="bl-cap-settings__unit">seconds</span>
        </span>
      </label>

      <label className="bl-cap-settings__row">
        <span className="bl-cap-settings__label">
          Duration
          <HelpTooltip label="What does Duration do?">
            How long the recording lasts in one go. For audio, the recording is sliced into 1-second
            clips when you click Extract.
          </HelpTooltip>
        </span>
        <span className="bl-cap-settings__input-with-unit">
          <input
            type="number"
            min={1}
            max={60}
            step={1}
            value={durationSeconds}
            onChange={(e) =>
              setDurationSeconds(Number.parseInt(e.target.value, 10) || defaults.durationSeconds)
            }
            aria-label="Duration (seconds)"
          />
          <span className="bl-cap-settings__unit">seconds</span>
        </span>
      </label>

      {onToggleDisabled && (
        <label className="bl-cap-settings__row bl-cap-settings__row--toggle">
          <span className="bl-cap-settings__label">
            Disable class
            <HelpTooltip label="What does Disable do?">
              Hides this class from training and testing without deleting it. Great for trying the
              model without one class to see if it’s actually helping.
            </HelpTooltip>
          </span>
          <input
            type="checkbox"
            checked={Boolean(cls.disabled)}
            onChange={(e) => void onToggleDisabled(e.target.checked)}
            aria-label="Disable class"
          />
        </label>
      )}

      <div className="bl-cap-settings__actions">
        <button
          type="button"
          className="bl-cap-settings__cancel"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="bl-cap-settings__save"
          onClick={() => void handleSave()}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
